import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Filter } from '~commonInfra/utils/Filter';

import { Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { imgPath } from '../../commonInfra/utils/ImgUrl';
import { FilterFieldNames } from '../../spacesPlatform/models/IFilterClause';
import { BackgroundJobStatus, BackgroundJobTaskType } from './BackgroundJobInfo';

export const AssociatedContentTypes = ['Workbook', 'Datasource', 'Flow', 'VirtualConnection'] as const;
export type AssociatedContentType = typeof AssociatedContentTypes[number];

export interface IBackgroundJob {
  jobId: number;
  taskLuid?: string | null;
  status: keyof typeof BackgroundJobStatus;
  priority: number; //[0-100] corresponding to column priority in public.background_jobs table
  taskType: keyof typeof BackgroundJobTaskType; //see below JobTaskType for valid values
  jobRequestedTime: string; // YYYY-MM-DDThh:mm:ssZ
  currentRunTime: number; //In seconds
  currentQueueTime: number; // In Seconds
  averageRunTime: number | undefined; //In Seconds
  averageQueueTime: number | undefined; //In Seconds
  jobDescription: string; // When there is no description this is an empty string
  jobCompletedTime: string; // YYYY-MM-DDThh:mm:ssZ
  taskLastSuccessTime: string | undefined; // the last time the task completed successfully
  overflow: boolean; // Whether the job is in the overflow queu
  associatedContentName: string | null; // Display name of the associated content (e.g., "Sales Dashboard")
  associatedContentId: string | null; // Numeric ID used to construct content URLs
  associatedContentType: AssociatedContentType | null; // Content type of associated content
}

export const JobEntityType = {
  Workbook: 'Workbook',
  DataSource: 'DataSource',
  PrepFlow: 'PrepFlow'
};

export function localizeBackgroundJobState(backgroundJob: IBackgroundJob): string {
  const isBridgeSpecificReportingEnabled = FeatureFlags.enableBridgeSpecificJobReportingEnabled;
  switch (getExtendedStatus(backgroundJob)) {
    case BackgroundJobStatus.InProgress:
    case BackgroundJobComputedStatus.InProgessOvertime:
      return Messages.backgroundJobStatusInProgress();
    case BackgroundJobStatus.Completed: {
      if(backgroundJob.taskType === BackgroundJobTaskType.Bridge && !isBridgeSpecificReportingEnabled) {
        return Messages.backgroundJobStatusCompletedBridge();
      }
      return Messages.backgroundJobStatusCompleted();
    }
    case BackgroundJobStatus.SentToBridge: {
        return Messages.backgroundJobStatusCompletedBridge();
    }
    case BackgroundJobComputedStatus.BridgeExtractionCompleted:{
      return Messages.backgroundJobStatusCompleted();
    }
    case BackgroundJobStatus.Failed:
    case BackgroundJobComputedStatus.FailedSuspended:
      return Messages.backgroundJobStatusFailed();
    case BackgroundJobStatus.Cancelled:
    case BackgroundJobComputedStatus.CancelledThrottled:
      return Messages.backgroundJobStatusCancelled();
    case BackgroundJobStatus.Pending:
    case BackgroundJobComputedStatus.PendingOvertime:
    case BackgroundJobComputedStatus.PendingOutOfQuota:
    default:
      return Messages.backgroundJobStatusPending();
  }
}

export function localizeBackgroundJobTaskType(taskType: keyof typeof BackgroundJobTaskType): string {
  switch (taskType) {
    case BackgroundJobTaskType.Bridge:
      return Messages.backgroundJobTaskTypeBridge();
    case BackgroundJobTaskType.Extract:
      return Messages.backgroundJobTaskTypeExtract();
    case BackgroundJobTaskType.Flow:
      return Messages.backgroundJobTaskTypeFlow();
    case BackgroundJobTaskType.Subscription:
      return Messages.backgroundJobTaskTypeSubscription();
    case BackgroundJobTaskType.Encryption:
      return Messages.backgroundJobTaskTypeEncryption();
    case BackgroundJobTaskType.System:
    default:
      return Messages.backgroundJobTaskTypeSystem();
  }
}

export enum BackgroundJobComputedStatus {
  PendingOvertime = 'PendingOvertime',
  PendingOutOfQuota = 'PendingOutOfQuota',
  InProgessOvertime = 'InProgressOvertime',
  FailedSuspended = 'FailedSuspended',
  CancelledThrottled = 'CancelledThrottled',
  BridgeExtractionCompleted = 'BridgeExtractionCompleted',
}

function isSuspended(backgroundJob: IBackgroundJob): boolean {
  // This gets it's own function as we expect that we will at some point move to a "cleaner" way
  return backgroundJob.jobDescription === 'Suspended: skipped\n';
}

function isOvertime(backgroundJob: IBackgroundJob): boolean {
  switch (backgroundJob.status) {
    case BackgroundJobStatus.Pending:
      // is false if averageQueueTime is undefined
      return backgroundJob.currentQueueTime > backgroundJob.averageQueueTime * 2;
    case BackgroundJobStatus.InProgress:
      // is false if averageRunTime is undefined
      return backgroundJob.currentRunTime > backgroundJob.averageRunTime * 1.1;
    default:
      false;
  }
}

function isThrottled(backgroundJob: IBackgroundJob): boolean {
  return backgroundJob.jobDescription?.startsWith('No more non-interactive quota remaining for your site.');
}

export function getExtendedStatus(backgroundJob: IBackgroundJob): BackgroundJobStatus | BackgroundJobComputedStatus {
  const status = backgroundJob.status;
  // Handle SentToBridge specifically since the enum key ('SentToBridge') differs from value ('Sent to Bridge')
  // Check if status is the key 'SentToBridge' or the value 'Sent to Bridge' (runtime may differ from TypeScript type)
  if (status === 'SentToBridge' || (status as string) === BackgroundJobStatus.SentToBridge) {
    return BackgroundJobStatus.SentToBridge;
  }
  if (status === BackgroundJobStatus.Pending && backgroundJob.overflow) {
    return BackgroundJobComputedStatus.PendingOutOfQuota;
  }
  if (status === BackgroundJobStatus.Pending && isOvertime(backgroundJob)) {
    return BackgroundJobComputedStatus.PendingOvertime;
  }
  if (status === BackgroundJobStatus.InProgress && isOvertime(backgroundJob)) {
    return BackgroundJobComputedStatus.InProgessOvertime;
  }
  if (status === BackgroundJobStatus.Failed && isSuspended(backgroundJob)) {
    return BackgroundJobComputedStatus.FailedSuspended;
  }
  if (status === BackgroundJobStatus.Cancelled && isThrottled(backgroundJob)) {
    return BackgroundJobComputedStatus.CancelledThrottled;
  }
  if (status === BackgroundJobStatus.BridgeExtractionCompleted) {
    return BackgroundJobComputedStatus.BridgeExtractionCompleted;
  }
  return BackgroundJobStatus[status];
}

export function getBackgroundJobFinishTime(backgroundJob: IBackgroundJob): string {
  return backgroundJob.jobCompletedTime !== ''
    ? L10n.localizedDateTime(backgroundJob.jobCompletedTime)
    : Messages.backgroundJobTooltipUndefined();
}

export function getBackgroundJobBridgeClient(backgroundJob: IBackgroundJob): string {
  const clientLabel = 'Bridge Client: ';
  return backgroundJob.jobDescription.startsWith(clientLabel)
    ? backgroundJob.jobDescription.substring(clientLabel.length)
    : null;
}

export function getIconForBackgroundJob(backgroundJob: IBackgroundJob): string {
  switch (getExtendedStatus(backgroundJob)) {
    case BackgroundJobComputedStatus.PendingOvertime:
      return imgPath('server_node_status_inprogress_active.svg');
    case BackgroundJobStatus.InProgress:
      return imgPath('flow_run_in_progress.svg');
    case BackgroundJobComputedStatus.InProgessOvertime:
      return imgPath('server_node_status_late_active.svg');
    case BackgroundJobStatus.Cancelled:
    case BackgroundJobComputedStatus.CancelledThrottled:
      return imgPath('flow_run_cancelled.svg');
    case BackgroundJobComputedStatus.FailedSuspended:
      return imgPath('server_node_status_suspended.svg');
    case BackgroundJobStatus.Completed:
    case BackgroundJobComputedStatus.BridgeExtractionCompleted:
    case BackgroundJobStatus.SentToBridge:
      return imgPath('flow_run_succeeded.svg');
    case BackgroundJobStatus.Failed:
      return imgPath('flow_run_error.svg');
    case BackgroundJobStatus.Pending:
    case BackgroundJobComputedStatus.PendingOutOfQuota:
    default:
      return imgPath('flow_run_pending.svg');
  }
}

export function getBackgroundJobThrottleTooltip(backgroundJob: IBackgroundJob): string {
  if (FeatureFlags.onPremiseGovernanceEnabled) {
    return Messages.backgroundJobTooltipCancelledOnPremThrottled({
      CANCEL_TIME: getBackgroundJobFinishTime(backgroundJob),
    });
  }
  if (backgroundJob.taskType === BackgroundJobTaskType.Flow) {
    return Messages.backgroundJobTooltipCancelledThrottledFlows({
      CANCEL_TIME: getBackgroundJobFinishTime(backgroundJob),
    });
  } else {
    return Messages.backgroundJobTooltipCancelledThrottled({
      CANCEL_TIME: getBackgroundJobFinishTime(backgroundJob),
    });
  }
}

export function getBackgroundJobConcurrencyTooltip(backgroundJob: IBackgroundJob): string {
  // This job has been queued for <mins> minutes and cannot run because your site has reached its concurrency limit for <extract refreshes/subscriptions/flows>.
  if (!FeatureFlags.onPremiseGovernanceEnabled) {
    return Messages.backgroundJobTooltipQueuedOutOfQuota({
      QUEUE_TIME: L10n.localizedFloat(backgroundJob.currentQueueTime / 60),
    });
  }
  switch (backgroundJob.taskType) {
    default:
    case BackgroundJobTaskType.Extract:
      return Messages.backgroundJobTooltipQueuedOnPremOutOfQuotaExtract({
        QUEUE_TIME: L10n.localizedFloat(backgroundJob.currentQueueTime / 60),
      });
    case BackgroundJobTaskType.Subscription:
      return Messages.backgroundJobTooltipQueuedOnPremOutOfQuotaSubscription({
        QUEUE_TIME: L10n.localizedFloat(backgroundJob.currentQueueTime / 60),
      });
  }
}

export function getBackgroundJobStatusTooltip(backgroundJob: IBackgroundJob): string {
  const isBridgeSpecificReportingEnabled = FeatureFlags.enableBridgeSpecificJobReportingEnabled;
  switch (getExtendedStatus(backgroundJob)) {
    case BackgroundJobComputedStatus.PendingOvertime:
      // averageQueueTime cannot be undefined if the status is PendingOvertime (see isOvertime)
      return Messages.backgroundJobTooltipQueuedOvertime({
        QUEUE_OVERTIME: L10n.localizedFloat((backgroundJob.currentQueueTime - backgroundJob.averageQueueTime) / 60),
        AVERAGE_QUEUE_TIME: L10n.localizedFloat(backgroundJob.averageQueueTime / 60),
      });
    case BackgroundJobComputedStatus.PendingOutOfQuota:
      return getBackgroundJobConcurrencyTooltip(backgroundJob);
    case BackgroundJobStatus.InProgress:
      return Messages.backgroundJobTooltipRunning({
        RUN_TIME: L10n.localizedFloat(backgroundJob.currentRunTime / 60),
      });
    case BackgroundJobComputedStatus.InProgessOvertime:
      // averageRunTime cannot be undefined if the status is InProgessOvertime (see isOvertime)
      return Messages.backgroundJobTooltipRunningOvertime({
        RUN_OVERTIME: L10n.localizedFloat((backgroundJob.currentRunTime - backgroundJob.averageRunTime) / 60),
        AVERAGE_RUN_TIME: L10n.localizedFloat(backgroundJob.averageRunTime / 60),
      });
    case BackgroundJobStatus.SentToBridge:
      const bridgeClient = getBackgroundJobBridgeClient(backgroundJob);
        return bridgeClient != null
        ? Messages.backgroundJobTooltipCompletedBridgeClient({
          COMPLETE_TIME: getBackgroundJobFinishTime(backgroundJob),
          BRIDGE_CLIENT: bridgeClient
        })
        : Messages.backgroundJobTooltipCompletedBridge({
          COMPLETE_TIME: getBackgroundJobFinishTime(backgroundJob)
        });
    case BackgroundJobStatus.Completed:
    {
      if (backgroundJob.taskType === BackgroundJobTaskType.Bridge) {
        const bridgeClient = getBackgroundJobBridgeClient(backgroundJob);
        if (isBridgeSpecificReportingEnabled) {
          return bridgeClient != null
          ? Messages.backgroundJobTooltipCompletedBridgeExtractionCompleted({
            BRIDGE_CLIENT: bridgeClient,
            COMPLETE_TIME: getBackgroundJobFinishTime(backgroundJob),
          })
          : Messages.backgroundJobTooltipCompletedBridge({
            COMPLETE_TIME: getBackgroundJobFinishTime(backgroundJob)
          });
        } else {
          return bridgeClient != null
          ? Messages.backgroundJobTooltipCompletedBridgeClient({
            COMPLETE_TIME: getBackgroundJobFinishTime(backgroundJob),
            BRIDGE_CLIENT: bridgeClient
          })
          : Messages.backgroundJobTooltipCompletedBridge({
            COMPLETE_TIME: getBackgroundJobFinishTime(backgroundJob)
          });
        }
      }

      return Messages.backgroundJobTooltipCompleted({
        COMPLETE_TIME: getBackgroundJobFinishTime(backgroundJob),
      });
    }
    case BackgroundJobComputedStatus.BridgeExtractionCompleted:
    {
      const bridgeClient = getBackgroundJobBridgeClient(backgroundJob);
      return Messages.backgroundJobTooltipCompletedBridgeExtractionCompleted({
        BRIDGE_CLIENT: bridgeClient,
        COMPLETE_TIME: getBackgroundJobFinishTime(backgroundJob),
      });
    }
    case BackgroundJobStatus.Cancelled:
      return Messages.backgroundJobTooltipCancelled({
        CANCEL_TIME: getBackgroundJobFinishTime(backgroundJob),
        RUN_TIME: L10n.localizedFloat(backgroundJob.currentRunTime / 60),
      });
    case BackgroundJobComputedStatus.CancelledThrottled:
      return getBackgroundJobThrottleTooltip(backgroundJob);
    case BackgroundJobStatus.Failed:
      return backgroundJob.jobCompletedTime !== ''
        ? Messages.backgroundJobTooltipFailed({
          FAIL_TIME: L10n.localizedDateTime(backgroundJob.jobCompletedTime),
          RUN_TIME: L10n.localizedFloat(backgroundJob.currentRunTime / 60),
          FAILURE_REASON: backgroundJob.jobDescription ? backgroundJob.jobDescription : Messages.backgroundJobNoReasonGiven(),
        })
        : Messages.backgroundJobTooltipFailedUnknownTime({
          RUN_TIME: L10n.localizedFloat(backgroundJob.currentRunTime / 60),
          FAILURE_REASON: backgroundJob.jobDescription ? backgroundJob.jobDescription : Messages.backgroundJobNoReasonGiven(),
        });
    case BackgroundJobComputedStatus.FailedSuspended:
      return Messages.backgroundJobTooltipSuspended();
    case BackgroundJobStatus.Pending:
    default:
      return Messages.backgroundJobTooltipQueued({
        QUEUE_TIME: L10n.localizedFloat(backgroundJob.currentQueueTime / 60),
      });
  }
}

export const nonSystemJobFilter = Filter.stringFieldInArray(
  FilterFieldNames.taskType,
  _.values(_.omit(BackgroundJobTaskType, BackgroundJobTaskType.System))
);
